<main>
  <loading-overlay [isLoading]="isLoading()"></loading-overlay>

  <!-- Header -->
  <header>
    <div class="headline">
      <h1>
        @if (!isNewRecord()) {
          {{ 'RemissionGuide_Title' | translateWF: 'Remission Guide' }}: {{ remissionGuide()?.number }}
        } @else {
          {{ 'RemissionGuide_New' | translateWF: 'New Remission Guide' }}
        }
      </h1>
      @if (!isNewRecord() && remissionGuide()) {
        <p-tag [value]="getStatusLabel(remissionGuide()!.status)" [severity]="getStatusSeverity(remissionGuide()!.status)" />
      }
    </div>

    <div class="buttons">
      @if (!isNewRecord()) {
        <p-button
          [label]="'RemissionGuide_Print_Report' | translateWF: 'Print Report'"
          icon="pi pi-print"
          variant="outlined"
          (onClick)="printReport()"
          [disabled]="isDownloading()"
          [loading]="isDownloading()"
        />
      }

      @if (canEdit()) {
        <p-button [label]="'Common_Save' | translateWF: 'Save'" icon="pi pi-save" (onClick)="onSave()" />
      }

      @if (canDelete()) {
        <p-button [label]="'Common_Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="onDelete()" />
      }

      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="onCancel()" />
    </div>
  </header>

  <!-- Content -->
  <div class="content">
    <form class="col" [formGroup]="mainForm">
      <!-- General Information -->
      <p-panel [header]="'RemissionGuide_General_Info' | translateWF: 'General Information'" [toggleable]="true" [collapsed]="false">
        <p-card data-variant="flat">
          <div class="grid-auto-3">
            <!-- Row 1: Dates -->
            <div class="form-box">
              <label>{{ 'RemissionGuide_Issued_Date' | translateWF: 'Issued Date' }} *</label>
              <p-datepicker fluid class="w-full" [iconDisplay]="'input'" [showIcon]="true" formControlName="issuedDate" appendTo="body" />
            </div>
            <div class="form-box">
              <label>{{ 'RemissionGuide_Transfer_Start' | translateWF: 'Transfer Start Date' }} *</label>
              <p-datepicker fluid class="w-full" [iconDisplay]="'input'" [showIcon]="true" formControlName="transferStartDate" appendTo="body" />
            </div>
            <div class="form-box">
              <label>{{ 'RemissionGuide_Transfer_End' | translateWF: 'Transfer End Date' }} *</label>
              <p-datepicker fluid class="w-full" [iconDisplay]="'input'" [showIcon]="true" formControlName="transferEndDate" appendTo="body" />
            </div>
            <!-- Row 2: Transfer Type, Status, Other Detail -->
            <div class="form-box">
              <label>{{ 'RemissionGuide_Transfer_Type' | translateWF: 'Transfer Type' }} *</label>
              <csl-dropdown-master-data
                [categoryCode]="'RemissionGuideTransferType'"
                formControlName="remissionGuideTransferTypeCode"
                [placeholder]="'RemissionGuide_Select_Transfer_Type' | translateWF: 'Select transfer type'"
                [allowCreate]="true"
              ></csl-dropdown-master-data>
            </div>
            <div class="form-box">
              <label>{{ 'Common_Status' | translateWF: 'Status' }}</label>
              <p-select
                fluid
                class="cs-select-truncate w-full"
                formControlName="status"
                [options]="statusSelectOptions()"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'RemissionGuide_Select_Status' | translateWF: 'Select status'"
                appendTo="body"
              />
            </div>

            <div class="form-box">
              <label>{{ 'RemissionGuide_Other_Detail' | translateWF: 'Other Transfer Detail' }}</label>
              <input
                pInputText
                class="w-full"
                formControlName="otherTransferTypeDetail"
                [placeholder]="'RemissionGuide_Enter_Other_Detail' | translateWF: 'Additional detail (optional)'"
              />
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Remitter Information -->
      <p-panel [header]="'RemissionGuide_Remitter_Info' | translateWF: 'Remitter Information'" [toggleable]="true" [collapsed]="false">
        <p-card data-variant="flat">
          <div class="grid-auto-2">
            <div class="form-box">
              <label>{{ 'RemissionGuide_Remitter' | translateWF: 'Remitter' }} *</label>
              <p-autocomplete
                fluid
                #remitterSearchRef="cslServerSearch"
                [cslServerSearch]="fetchRemitters"
                [suggestions]="remitterSearchRef.results()"
                (onSelect)="onRemitterSelect($event.value)"
                (onClear)="onRemitterClear()"
                [dropdown]="true"
                [ngModel]="selectedRemitter()"
                [ngModelOptions]="{ standalone: true }"
                optionLabel="name"
                [forceSelection]="true"
                class="w-full"
                [showClear]="true"
                appendTo="body"
                [placeholder]="'RemissionGuide_Search_Remitter' | translateWF: 'Search remitter...'"
              ></p-autocomplete>
            </div>
            <div class="form-box">
              <label>{{ 'RemissionGuide_Departure_Place' | translateWF: 'Departure Place' }} *</label>
              <p-select
                fluid
                class="cs-select-truncate w-full"
                formControlName="departurePlaceCode"
                [options]="remitterAddressOptions()"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'RemissionGuide_Select_Departure' | translateWF: 'Select departure address'"
                [showClear]="true"
                appendTo="body"
              >
                <ng-template pTemplate="selectedItem" let-selectedOption>
                  <div class="select-ellipsis" [title]="selectedOption?.label || ('RemissionGuide_Select_Departure' | translateWF: 'Select departure address')">
                    {{
                      selectedOption?.label ||
                        ('RemissionGuide_Select_Departure'
                          | translateWF
                            : 'Select departure
                    address')
                    }}
                  </div>
                </ng-template>

                <ng-template pTemplate="item" let-option>
                  <div class="select-option-ellipsis" [title]="option?.label">
                    {{ option?.label }}
                  </div>
                </ng-template>
              </p-select>
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Consignee Information -->
      <p-panel [header]="'RemissionGuide_Consignee_Info' | translateWF: 'Consignee Information'" [toggleable]="true" [collapsed]="false">
        <p-card data-variant="flat">
          <div class="grid-auto-2">
            <div class="form-box">
              <label>{{ 'RemissionGuide_Consignee' | translateWF: 'Consignee' }} *</label>
              <p-autocomplete
                fluid
                #consigneeSearchRef="cslServerSearch"
                [cslServerSearch]="fetchConsignees"
                [suggestions]="consigneeSearchRef.results()"
                (onSelect)="onConsigneeSelect($event.value)"
                (onClear)="onConsigneeClear()"
                [dropdown]="true"
                class="w-full"
                [ngModel]="selectedConsignee()"
                [ngModelOptions]="{ standalone: true }"
                optionLabel="name"
                [forceSelection]="true"
                [showClear]="true"
                appendTo="body"
                [placeholder]="'RemissionGuide_Search_Consignee' | translateWF: 'Search consignee...'"
              ></p-autocomplete>
            </div>
            <div class="form-box">
              <label>{{ 'RemissionGuide_Destination_Place' | translateWF: 'Destination Place' }} *</label>
              <p-select
                fluid
                class="cs-select-truncate w-full"
                formControlName="destinationPlaceCode"
                [options]="consigneeAddressOptions()"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'RemissionGuide_Select_Destination' | translateWF: 'Select destination address'"
                [showClear]="true"
                appendTo="body"
              >
                <ng-template pTemplate="selectedItem" let-selectedOption>
                  <div class="select-ellipsis" [title]="selectedOption?.label || ('RemissionGuide_Select_Destination' | translateWF: 'Select destination address')">
                    {{
                      selectedOption?.label ||
                        ('RemissionGuide_Select_Destination'
                          | translateWF
                            : 'Select
                    destination address')
                    }}
                  </div>
                </ng-template>

                <ng-template pTemplate="item" let-option>
                  <div class="select-option-ellipsis" [title]="option?.label">
                    {{ option?.label }}
                  </div>
                </ng-template>
              </p-select>
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Line Items -->
      <p-panel [header]="'RemissionGuide_Lines' | translateWF: 'Line Items'" [toggleable]="true" [collapsed]="false">
        <div class="col-xs">
          <p-panel [header]="'RemissionGuide_Search_Item' | translateWF: 'Search Item Code'" data-tone="surface">
            <ng-template pTemplate="icons">
              @if (canEdit()) {
                <p-autocomplete
                  fluid
                  #itemSearchRef="cslServerSearch"
                  [cslServerSearch]="fetchItems"
                  [suggestions]="itemSearchRef.results()"
                  (onSelect)="onItemSelect($event.value)"
                  [(ngModel)]="selectedItemForLine"
                  [ngModelOptions]="{ standalone: true }"
                  optionLabel="itemCode"
                  [forceSelection]="true"
                  [showClear]="true"
                  appendTo="body"
                  size="small"
                  [placeholder]="'RemissionGuide_Search_Item_Placeholder' | translateWF: 'Search by item code...'"
                >
                  <ng-template let-item #item>
                    <div>
                      <span class="font-semibold">{{ item.itemCode }}</span>
                      <span class="ml-2 text-surface-500">{{ getItemDisplayLabel(item) }}</span>
                    </div>
                  </ng-template>
                </p-autocomplete>
                <p-button [label]="'RemissionGuide_Add_Line' | translateWF: 'Add Line'" icon="pi pi-plus" variant="outlined" severity="secondary" (onClick)="addLine()" />
              }
            </ng-template>
            <p-card data-variant="compact">
              <p-table [value]="linesFormArray.controls" [formGroup]="mainForm" [rowHover]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'RemissionGuide_Item' | translateWF: 'Item' }}</th>
                    <th>{{ 'RemissionGuide_Quantity' | translateWF: 'Quantity' }} *</th>
                    <th style="width: 110px">{{ 'RemissionGuide_UOM' | translateWF: 'UOM' }}</th>
                    <th>{{ 'RemissionGuide_Gross_Weight' | translateWF: 'Gross Weight (Kg)' }}</th>
                    <th>{{ 'RemissionGuide_Package_Count' | translateWF: 'Bultos' }}</th>
                    <th>{{ 'RemissionGuide_Unit_Price' | translateWF: 'Unit Price (USD)' }}</th>
                    <th>{{ 'RemissionGuide_Total_Price' | translateWF: 'Total Price (USD)' }}</th>
                    @if (canEdit()) {
                      <th style="width: 80px"></th>
                    }
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-lineControl let-i="rowIndex">
                  <tr [formGroup]="lineControl">
                    @if (canEdit()) {
                      <td>
                        @if (isLineFromCatalog(lineControl)) {
                          <strong>({{ lineControl.controls.itemCode.value }})</strong>
                          <span class="ml-1">{{ lineControl.controls.description.value || '' }}</span>
                        } @else {
                          <input pInputText formControlName="description" class="w-full" [placeholder]="'RemissionGuide_Enter_Description' | translateWF: 'Enter description...'" />
                        }
                      </td>
                      <td>
                        <p-inputnumber fluid formControlName="quantity" [minFractionDigits]="0" [maxFractionDigits]="2" [min]="0.01" />
                      </td>
                      <td>
                        @if (isLineFromCatalog(lineControl)) {
                          {{ getUomLabel(lineControl.controls.uom.value) }}
                        } @else {
                          <p-select
                            fluid
                            class="cs-select-truncate w-full"
                            formControlName="uom"
                            [options]="uomOptions"
                            optionLabel="label"
                            optionValue="value"
                            [placeholder]="'RemissionGuide_Select_UOM' | translateWF: 'UOM'"
                            [showClear]="true"
                            appendTo="body"
                          />
                        }
                      </td>
                      <td>
                        <p-inputnumber fluid formControlName="grossWeightKg" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="3" />
                      </td>
                      <td>
                        <p-inputnumber fluid formControlName="packageCount" [min]="0" [showButtons]="false" />
                      </td>
                      <td>{{ calculateUnitPrice(lineControl) | number: '1.2-2' }}</td>
                      <td>
                        <p-inputnumber fluid formControlName="unitPriceUSD" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" />
                      </td>
                      <td class="text-center">
                        <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (onClick)="removeLine(i)" />
                      </td>
                    } @else {
                      <td>
                        @if (isLineFromCatalog(lineControl)) {
                          <strong>({{ lineControl.controls.itemCode.value }})</strong>
                          <span class="ml-1">{{ lineControl.controls.description.value || '' }}</span>
                        } @else {
                          {{ lineControl.controls.description.value || '-' }}
                        }
                      </td>
                      <td>{{ lineControl.controls.quantity.value | number: '1.0-2' }}</td>
                      <td>{{ getUomLabel(lineControl.controls.uom.value) }}</td>
                      <td>{{ lineControl.controls.grossWeightKg.value | number: '1.2-3' }}</td>
                      <td>{{ lineControl.controls.packageCount.value ?? '-' }}</td>
                      <td>{{ calculateUnitPrice(lineControl) | number: '1.2-2' }}</td>
                      <td>{{ lineControl.controls.unitPriceUSD.value | number: '1.2-2' }}</td>
                    }
                  </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td [attr.colspan]="canEdit() ? 8 : 7" class="text-center py-4">
                      {{ 'RemissionGuide_No_Lines' | translateWF: 'No line items added' }}
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
          </p-panel>
        </div>
      </p-panel>

      <!-- Transport Info -->
      <p-panel [header]="'RemissionGuide_Transport_Info' | translateWF: 'Transport Information'" [toggleable]="true" [collapsed]="true">
        <p-card data-variant="flat">
          <div formGroupName="transportInfo">
            <div class="grid-auto-3">
              <div class="form-box">
                <label>{{ 'RemissionGuide_Carrier_Name' | translateWF: 'Carrier Name' }}</label>
                <input pInputText class="w-full" formControlName="carrierName" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Carrier_Tax_Id' | translateWF: 'Carrier Tax ID' }}</label>
                <input pInputText class="w-full" formControlName="carrierTaxId" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Driver_Name' | translateWF: 'Driver Name' }}</label>
                <input pInputText class="w-full" formControlName="driverName" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Driver_Tax_Id' | translateWF: 'Driver Tax ID' }}</label>
                <input pInputText class="w-full" formControlName="driverTaxId" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Vehicle_Id' | translateWF: 'Vehicle ID' }}</label>
                <input pInputText class="w-full" formControlName="vehicleId" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_License_Number' | translateWF: 'License Number' }}</label>
                <input pInputText class="w-full" formControlName="driverLicenseNumber" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Tractor_Id' | translateWF: 'Tractor ID' }}</label>
                <input pInputText class="w-full" formControlName="tractorId" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Seal_Number' | translateWF: 'Seal Number' }}</label>
                <input pInputText class="w-full" formControlName="sealNumber" />
              </div>
              <div class="form-box">
                <label>{{ 'RemissionGuide_Authorized_By' | translateWF: 'Authorized By' }}</label>
                <input pInputText class="w-full" formControlName="authorizedByUsername" />
              </div>
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Footer Message -->
      <p-panel [header]="'RemissionGuide_Footer' | translateWF: 'Footer Message'" [toggleable]="true" [collapsed]="true">
        <p-card data-variant="flat">
          <div class="form-box">
            <textarea
              pInputTextarea
              class="w-full"
              formControlName="footerMessage"
              rows="3"
              [placeholder]="'RemissionGuide_Enter_Footer' | translateWF: 'Enter footer message (optional)'"
            ></textarea>
          </div>
        </p-card>
      </p-panel>

      <!-- Status History -->
      @if (!isNewRecord() && remissionGuide()?.statusHistory?.length) {
        <p-panel [header]="'RemissionGuide_Status_History' | translateWF: 'Status History'" [toggleable]="true" [collapsed]="true">
          <p-card data-variant="compact">
            <p-table [value]="remissionGuide()!.statusHistory" [rowHover]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'Common_Date' | translateWF: 'Date' }}</th>
                  <th>{{ 'Common_Status' | translateWF: 'Status' }}</th>
                  <th>{{ 'Common_User' | translateWF: 'User' }}</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-entry>
                <tr>
                  <td>{{ entry.date | date: 'medium' }}</td>
                  <td>
                    <p-tag [value]="getStatusLabel(entry.status)" [severity]="getStatusSeverity(entry.status)" />
                  </td>
                  <td>{{ entry.user?.firstName }} {{ entry.user?.lastName }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>
      }
    </form>
  </div>
</main>

<p-toast position="bottom-right" />
<p-confirmdialog />
